import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { GeneratorService } from './generator.service';
import { switchMap } from 'rxjs/operators';
import { NbDialogService } from '@nebular/theme';

interface ScrapingTask {
    taskId: string;
    productId: string | null;
    timestamp: number;
}

@Injectable({
    providedIn: 'root'
})
export class ScrappedImagesService implements OnDestroy {
    private productsSource = new BehaviorSubject<Map<string | null, any>>(new Map());
    currentProducts = this.productsSource.asObservable();

    private pendingTasks: ScrapingTask[] = [];
    private pollingSubscription: Subscription | null = null;

    constructor(
        private generatorService: GeneratorService,
        private dialogService: NbDialogService // You might need to inject the template or handle error differently
    ) { }

    updateProducts(product: { id: string | null; urls: any; }) {
        const currentProducts = this.productsSource.value;
        currentProducts.set(product.id, product.urls);
        // Clean up any pending task for this product if manual update happens
        this.pendingTasks = this.pendingTasks.filter(t => t.productId !== product.id);
        this.productsSource.next(new Map(currentProducts)); // Create new reference
    }

    startScraping(id: string | null, url_image: string, url_html: string, errorTemplate: any) {
        this.generatorService.scrapImageProductPage(url_image, url_html).subscribe(
            (response) => {
                if (response.task_id) {
                    this.pendingTasks.push({
                        taskId: response.task_id,
                        productId: id,
                        timestamp: Date.now()
                    });
                    this.startPolling();
                } else if (response.urls) {
                    // Legacy sync fallback
                    this.updateProducts({ id, urls: response.urls });
                }
            },
            (error) => {
                if (errorTemplate) this.dialogService.open(errorTemplate);
            }
        );
    }

    private startPolling() {
        if (this.pollingSubscription && !this.pollingSubscription.closed) {
            return;
        }

        this.pollingSubscription = interval(3000).pipe(
            switchMap(() => this.generatorService.getTaskProgressList('scrap_image'))
        ).subscribe(
            (tasks: any[]) => {
                this.processTaskUpdates(tasks);
                // Stop polling if no tasks left
                if (this.pendingTasks.length === 0) {
                    this.stopPolling();
                }
            },
            (error) => {
                console.error("Polling error", error);
                // Don't stop polling on transient errors, but maybe backup logic?
            }
        );
    }

    private processTaskUpdates(backendTasks: any[]) {
        if (!backendTasks || !Array.isArray(backendTasks)) return;

        const remainingTasks: ScrapingTask[] = [];

        this.pendingTasks.forEach(localTask => {
            const backendTask = backendTasks.find(t => t.task_id === localTask.taskId);

            if (backendTask) {
                if (backendTask.status === 'SUCCESS') {
                    const urls = backendTask.images?.urls || [];
                    this.updateProducts({ id: localTask.productId, urls: urls });
                } else if (backendTask.status === 'FAILED') {
                    // Handle failure (maybe notify user or just stop tracking)
                    console.warn(`Task ${localTask.taskId} failed`);
                } else {
                    // Still pending/running
                    remainingTasks.push(localTask);
                }
            } else {
                // Task not found in backend list? Maybe completed and cleared? 
                // Or list filtering logic mismatch. Keep it for now or timeout.
                // Simple timeout logic:
                if (Date.now() - localTask.timestamp < 300000) { // 5 min timeout
                    remainingTasks.push(localTask);
                }
            }
        });

        this.pendingTasks = remainingTasks;

        if (this.pendingTasks.length === 0) {
            this.stopPolling();
        }
    }

    private stopPolling() {
        if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = null;
        }
    }

    clearProducts() {
        this.productsSource.next(new Map());
        this.pendingTasks = [];
        this.stopPolling();
    }

    ngOnDestroy() {
        this.stopPolling();
    }
}